import type { AxiosInstance } from 'axios';

import axiosImport from './api';

const axios = axiosImport as unknown as AxiosInstance;

interface ListVideoBookmarksResponseOptions {
  responseId: number;
  questionId: number;
  signal?: AbortSignal;
}

interface ListVideoBookmarksSurveyOptions {
  surveyRef: number;
  signal?: AbortSignal;
}

interface CreateVideoBookmarkOptions {
  responseId: number;
  questionId: number;
  text: string;
  start_ts: number;
  end_ts: number;
  item_indices: number[];
  signal?: AbortSignal;
}

interface DeleteVideoBookmarkOptions {
  bookmarkId: number;
  signal?: AbortSignal;
}

interface ListVideoFavoritesSurveyOptions {
  surveyRef: number;
  signal?: AbortSignal;
}

interface CreateVideoFavoriteOptions {
  responseId: number;
  questionId: number;
  signal?: AbortSignal;
}

interface DeleteVideoFavoriteOptions {
  favoriteId: number;
  signal?: AbortSignal;
}

interface VideoBookmark {
  id: number;
  survey_response: number;
  text: string;
  start_ts: number;
  end_ts: number;
  item_indices: number[];
  created_at?: string;
  updated_at?: string;
}

interface ListVideoBookmarksResponse {
  results: VideoBookmark[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

interface ListVideoBookmarksSurvey {
  results: VideoBookmark[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

interface VideoFavorite {
  id: number;
  survey_response: number;
  question_id: number;
  created_at?: string;
  updated_at?: string;
}

interface ListVideoFavoritesSurvey {
  results: VideoFavorite[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

/**
 * List video bookmarks for a survey
 * @param {Object} options
 * @param {number} options.surveyRef - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoBookmarksSurvey | VideoBookmark[]>}
 */
export const listVideoBookmarksSurvey = async ({
  signal,
  surveyRef,
}: ListVideoBookmarksSurveyOptions): Promise<ListVideoBookmarksSurvey | VideoBookmark[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      survey: surveyRef,
    },
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as ListVideoBookmarksSurvey | VideoBookmark[];
};

/**
 * List video bookmarks for a survey response
 * @param {Object} options
 * @param {number} options.questionId - The question ID
 * @param {number} options.responseId - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoBookmarksResponse | VideoBookmark[]>}
 */
export const listVideoBookmarksResponse = async ({
  questionId,
  responseId,
  signal,
}: ListVideoBookmarksResponseOptions): Promise<ListVideoBookmarksResponse | VideoBookmark[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      survey_response: responseId,
      question_id: questionId,
    },
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as ListVideoBookmarksResponse | VideoBookmark[];
};

/**
 * Create a video bookmark
 * @param {Object} options
 * @param {number} options.end_ts - The end timestamp in seconds
 * @param {number[]} options.item_indices - Array of item indices
 * @param {number} options.questionId - The question ID
 * @param {number} options.responseId - The survey response ID
 * @param {AbortSignal} [options.signal]
 * @param {number} options.start_ts - The start timestamp in seconds
 * @param {string} options.text - The bookmarked text
 * @returns {Promise<VideoBookmark>}
 */
export const createVideoBookmark = async ({
  end_ts,
  item_indices,
  questionId,
  responseId,
  signal,
  start_ts,
  text,
}: CreateVideoBookmarkOptions): Promise<VideoBookmark> => {
  const { data } = await axios({
    data: {
      survey_response: responseId,
      question_id: questionId,
      start_ts,
      end_ts,
      text,
      item_indices,
    },
    method: 'post',
    signal,
    url: '/survey/response/video_bookmark/',
  });
  return data as VideoBookmark;
};

/**
 * Delete a video bookmark
 * @param {Object} options
 * @param {number} options.bookmarkId - The bookmark ID to delete
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export const deleteVideoBookmark = async ({
  bookmarkId,
  signal,
}: DeleteVideoBookmarkOptions): Promise<void> => {
  await axios({
    method: 'delete',
    signal,
    url: `/survey/response/video_bookmark/${bookmarkId}/`,
  });
};

/**
 * List video favorites for a survey
 * @param {Object} options
 * @param {number} options.surveyRef - The survey ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<ListVideoFavoritesSurvey | VideoFavorite[]>}
 */
export const listVideoFavoritesSurvey = async ({
  signal,
  surveyRef,
}: ListVideoFavoritesSurveyOptions): Promise<ListVideoFavoritesSurvey | VideoFavorite[]> => {
  const { data } = await axios({
    method: 'get',
    params: {
      survey: surveyRef,
    },
    signal,
    url: '/survey/response/video_favorite/',
  });
  return data as ListVideoFavoritesSurvey | VideoFavorite[];
};

/**
 * Create a video favorite
 * @param {Object} options
 * @param {number} options.responseId - The survey response ID
 * @param {number} options.questionId - The question ID
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<VideoFavorite>}
 */
export const createVideoFavorite = async ({
  questionId,
  responseId,
  signal,
}: CreateVideoFavoriteOptions): Promise<VideoFavorite> => {
  const { data } = await axios({
    data: {
      survey_response: responseId,
      question_id: questionId,
    },
    method: 'post',
    signal,
    url: '/survey/response/video_favorite/',
  });
  return data as VideoFavorite;
};

/**
 * Delete a video favorite
 * @param {Object} options
 * @param {number} options.favoriteId - The favorite ID to delete
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<void>}
 */
export const deleteVideoFavorite = async ({
  favoriteId,
  signal,
}: DeleteVideoFavoriteOptions): Promise<void> => {
  await axios({
    method: 'delete',
    signal,
    url: `/survey/response/video_favorite/${favoriteId}/`,
  });
};
